import type {
  AiKnownOperation,
  AiKnownOperationKind,
  AiOperationEnvelope,
  AiOperationTargetRef,
  AiOperationalPlan,
  AiOperationalRequest,
  AiOperationalRiskLevel,
  AiRollbackPlan,
} from '../../../types/aiOperational'
import type { ChecklistItem } from '../../../types/schema'
import { retrieveOperationalContext, type AiOperationalContextWindow, type AiRetrievalElement } from '../context/operationalRetrieval'
import type { AiRetrievalRecord } from '../retrieval/retrievalTypes'
import { buildWorkspaceOperationalMemory } from '../memory/workspaceOperationalMemory'
import { buildExecutionPlan } from '../planning/executionPlanning'
import { buildExecutionGraph } from '../execution/executionGraph'
import { buildExecutionSimulation } from '../execution/executionSimulation'
import { buildOperationalForecasts } from '../planning/operationalForecasting'
import { buildOperationalIntelligencePreview, detectOperationalIntelligenceIntent } from '../planning/operationalIntelligence'
import { buildOperationalContinuity } from '../planning/operationalContinuity'
import { renderOperationalPrompt } from '../prompts/promptFragments'
import { hashDiagnosticIdentifier, recordAiExecutionTrace } from '../../diagnostics/operationalDiagnostics'

export interface LineOperationalOrchestratorInput extends AiOperationalRequest {
  elements: AiRetrievalElement[]
  checklistItems: ChecklistItem[]
  pulseHints?: Array<{ id: string; summary: string; severity?: string; targetId?: string }>
  supplementalRecords?: AiRetrievalRecord[]
}

const destructivePattern = /\b(delete|destroy|purge|wipe|erase|remove permanently|reset workspace|revoke|transfer credits|wallet|permission|admin|invite|integration|activate automation)\b/i
const mutationPattern = /\b(create|add|change|update|edit|modify|set|rename|move|complete|mark|link|unlink|dependency|checklist|timeline|goal|task|subtask)\b/i
const lowRiskPattern = /\b(summarize|explain|preview|show|navigate|open|guide|tour|walkthrough|report preview|suggest automation|organize chat)\b/i

const operationId = (kind: AiKnownOperationKind) =>
  `op_${kind.toLowerCase()}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

const targetFromRecord = (record: AiOperationalContextWindow['primary'][number]): AiOperationTargetRef => ({
  id: record.id,
  type: record.type,
  title: record.title,
  board: record.board,
  parentId: record.parentId,
})

const mergeTargets = (selected: AiOperationTargetRef[] | undefined, context: AiOperationalContextWindow) => {
  const targets = [...(selected ?? []), ...context.primary.slice(0, 5).map(targetFromRecord)]
  const seen = new Set<string>()
  return targets.filter((target) => {
    const key = `${target.type}:${target.id}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const classifyIntent = (request: AiOperationalRequest): {
  risk: AiOperationalRiskLevel
  nextStep: AiOperationalPlan['nextStep']
  operationKind: AiKnownOperationKind
  explanation: string
} => {
  const prompt = request.prompt.trim()
  if (destructivePattern.test(prompt)) {
    return {
      risk: 'RESTRICTED',
      nextStep: 'REFUSE',
      operationKind: 'PROPOSAL_CREATE',
      explanation: 'Restricted surface — admin, economy, permissions, integrations, automation activation, or destructive workspace changes must never execute from Line.',
    }
  }
  const intelligenceIntent = detectOperationalIntelligenceIntent(request)
  if (intelligenceIntent) {
    return {
      risk: 'LOW',
      nextStep: 'SHOW_PLAN',
      operationKind: intelligenceIntent.operationKind,
      explanation: intelligenceIntent.explanation,
    }
  }
  if (/\b(pulse|health|risk|readiness)\b/i.test(prompt) || request.surface === 'WORKSPACE_PULSE') {
    return {
      risk: 'LOW',
      nextStep: 'ANSWER',
      operationKind: 'PULSE_SUMMARY',
      explanation: 'Workspace Pulse context can be summarized without persistent mutation.',
    }
  }
  if (lowRiskPattern.test(prompt)) {
    return {
      risk: 'LOW',
      nextStep: request.mode === 'EXECUTE' ? 'SHOW_PLAN' : 'ANSWER',
      operationKind: /\b(tour|walkthrough|guide)\b/i.test(prompt) ? 'GUIDED_TOUR' : 'NAVIGATION',
      explanation: 'This can be handled as guidance, navigation, preview, or local UI work.',
    }
  }
  if (mutationPattern.test(prompt)) {
    const checklistKind = /\b(checklist|dependency|complete|mark|status)\b/i.test(prompt)
    return {
      risk: 'HIGH',
      nextStep: 'CREATE_PROPOSAL',
      operationKind: checklistKind ? 'CHECKLIST_STATUS' : 'TIMELINE_EDIT',
      explanation: 'Timeline and checklist mutations must be routed through reviewable proposals and trusted execution.',
    }
  }
  return {
    risk: 'MEDIUM',
    nextStep: 'CONFIRM',
    operationKind: 'PROPOSAL_CREATE',
    explanation: 'Ambiguous intent — Line needs a natural clarifying turn before drafting.',
  }
}

const rollbackForRisk = (risk: AiOperationalRiskLevel, operationId: string, operationKind: AiKnownOperationKind): AiRollbackPlan => {
  if (risk === 'HIGH') {
    return {
      strategy: 'AUDIT_REPLAY',
      reversible: true,
      contract: {
        id: `rollback_${operationId}`,
        strategy: 'AUDIT_REPLAY',
        restorationMode: 'AUDIT_REPLAY',
        authority: 'AUDIT_LEDGER',
        operationIds: [operationId],
        notes: [
          operationKind === 'CHECKLIST_STATUS'
            ? 'Status mutation rollback should reuse the existing audit/DeltaProcessor undo path.'
            : 'Rollback contract is metadata-only until a trusted execution path captures a concrete inverse.',
        ],
      },
      notes: ['Use the existing audit/undo path where available; checkpoint concrete inverse operations during trusted execution.'],
    }
  }
  if (risk === 'LOW') return { strategy: 'NOT_REVERSIBLE', reversible: false, notes: ['No persistent mutation is planned.'] }
  return {
    strategy: 'SNAPSHOT_RESTORE',
    reversible: true,
    notes: ['Capture a trusted execution snapshot before mutation once this becomes executable.'],
  }
}

const buildOperation = (
  request: AiOperationalRequest,
  context: AiOperationalContextWindow,
  intent: ReturnType<typeof classifyIntent>,
): AiKnownOperation => {
  const id = operationId(intent.operationKind)
  const targetRefs = mergeTargets(request.selectedTargets, context)
  const requiresReview = intent.risk === 'HIGH' || intent.risk === 'RESTRICTED'
  const requiresConfirmation = intent.risk === 'MEDIUM'
  return {
    id,
    kind: intent.operationKind,
    title: intent.operationKind.replace(/_/g, ' ').toLowerCase(),
    description: request.prompt.trim(),
    targetRefs,
    risk: intent.risk,
    requiresReview,
    requiresConfirmation,
    allowedAutoExecute: intent.risk === 'LOW',
    payload: {
      sourcePrompt: request.prompt,
      mode: request.mode,
    },
    rollback: rollbackForRisk(intent.risk, id, intent.operationKind),
  }
}

export const createLineOperationalPlan = (input: LineOperationalOrchestratorInput): AiOperationalPlan => {
  const startedAt = Date.now()
  const request: AiOperationalRequest = {
    workspaceId: input.workspaceId,
    actor: input.actor,
    surface: input.surface,
    prompt: input.prompt,
    mode: input.mode,
    selectedTargets: input.selectedTargets,
  }
  const retrievedContext = retrieveOperationalContext({
    workspaceId: input.workspaceId,
    query: input.prompt,
    selectedTargets: input.selectedTargets,
    elements: input.elements,
    checklistItems: input.checklistItems,
    pulseHints: input.pulseHints,
    supplementalRecords: input.supplementalRecords,
  })
  const memoryInsights = buildWorkspaceOperationalMemory({ request, context: retrievedContext })
  const forecasts = buildOperationalForecasts({ request, context: retrievedContext })
  const baseContext: AiOperationalContextWindow = {
    ...retrievedContext,
    digest: {
      ...retrievedContext.digest,
      memoryInsights,
      forecasts,
    },
  }
  const continuity = buildOperationalContinuity({ request, context: baseContext })
  const context: AiOperationalContextWindow = {
    ...baseContext,
    digest: {
      ...baseContext.digest,
      recommendations: continuity.recommendations,
      collaborationSignals: continuity.collaborationSignals,
      continuityThreads: continuity.threads,
    },
  }
  const intent = classifyIntent(request)
  const intelligencePreview = buildOperationalIntelligencePreview(request, context)
  const operation = buildOperation(request, context, intent)
  if (intelligencePreview) {
    operation.payload = {
      ...operation.payload,
      intelligencePreview,
    }
  }
  const envelope: AiOperationEnvelope = {
    version: 1,
    workspaceId: request.workspaceId,
    createdAtIso: new Date().toISOString(),
    sourceSurface: request.surface,
    risk: intent.risk,
    reviewRequired: operation.requiresReview,
    operations: [operation],
    audit: {
      summary: intent.explanation,
      rationale: [
        `Classified as ${intent.risk}.`,
        operation.requiresReview ? 'Persistent changes require proposal review.' : 'No high-impact mutation is planned.',
      ],
      sourceSurface: request.surface,
      actor: request.actor,
      workspaceId: request.workspaceId,
    },
    simulation: buildExecutionSimulation({
      operation,
      intent,
      ...(intelligencePreview ? { intelligencePreview } : {}),
      forecasts,
      continuity,
    }),
    contextDigest: context.digest,
  }
  envelope.executionPlan = buildExecutionPlan(envelope)
  envelope.executionGraph = buildExecutionGraph(envelope, envelope.executionPlan)
  void recordAiExecutionTrace({
    traceId: `ai_plan_${hashDiagnosticIdentifier(`${request.workspaceId}:${request.actor.uid}:${request.prompt}:${envelope.createdAtIso}`) ?? Date.now().toString(36)}`,
    workspaceId: request.workspaceId,
    operationHash: hashDiagnosticIdentifier(request.prompt) ?? 'h_empty',
    actorHash: hashDiagnosticIdentifier(request.actor.uid),
    surface: request.surface,
    mode: request.mode,
    routingDecision: intent.operationKind,
    risk: intent.risk,
    nextStep: intent.nextStep,
    executionGraphSize: envelope.executionGraph.steps.length,
    blockedCheckpointCount: envelope.executionGraph.checkpoints.filter((checkpoint) => checkpoint.state === 'BLOCKED' || checkpoint.state === 'WAITING').length,
    rollbackAvailable: envelope.executionGraph.rollbackContracts.length > 0,
    clarificationRequested: intent.nextStep === 'CONFIRM',
    lowConfidence: intent.risk === 'MEDIUM',
    retryCount: 0,
    createdAtIso: envelope.createdAtIso,
    latencyMs: Date.now() - startedAt,
  })

  return {
    request,
    envelope,
    nextStep: intent.nextStep,
    explanation: renderOperationalPrompt({ request, context, envelope }),
  }
}
